import React from 'react';
import classNames from 'classnames';
import styles from '../styles.module.css';
import ShapeIcon from '../shapes/ShapeIcon';
import {
  type SpriteSvgItem,
  type IconData,
  type DistributeUnion,
  type IconWithOffset,
  type NormalizedIcon,
  type PreparedIcon,
} from '../types';
import SearchBox from './SearchBox';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import ToolButton from '../toolbars/ToolButton';
import { type IconTabItem } from '../toolbars/ObjectMenu/IconsTab';
import { iconDisplayName, RenderSpriteAsIcon, testId } from './objectMenu';
import { SpriteSvg } from '../toolbars/ObjectMenu/SpriteSvg';
import { isShapeIcon, isShapeSpriteIcon } from '../objects/guards';
import type WhiteboardController from '../controllers/WhiteboardController';
import WithHint from '../toolbars/WithHint';
import { type ObjectMenuItem } from '../toolbars/ObjectMenu/ObjectMenuController';
import { svgPathToIcon, svgToIcon } from './svg';

export const isFaIcon = (iconData: ObjectMenuItem): iconData is IconData => {
  return !!('iconName' in iconData && iconData.iconName);
};

export const isSpriteIcon = (
  iconData: ObjectMenuItem
): iconData is SpriteSvgItem => {
  return !!('path' in iconData && iconData.path);
};

interface RenderIconProps {
  iconData: IconData;
}

export const RenderFaIcon = ({ iconData }: RenderIconProps) => (
  <i
    className={classNames(
      styles.iconPickerIcon,
      `${iconData.prefix} fa-${iconData.iconName}`
    )}
  />
);

export const iconComponent = (item: IconTabItem | undefined | null) => {
  if (!item) {
    return null;
  }

  return isFaIcon(item) ? (
    <RenderFaIcon iconData={item} />
  ) : (
    <RenderSpriteAsIcon item={item} />
  );
};

export const iconList = (): IconData[] => {
  return Object.values(ShapeIcon.iconDB);
};

export const filterIconList = (filterText: string): IconData[] => {
  const lowerFilterText = filterText.toLowerCase();
  return iconList().filter(icon =>
    icon.synonyms.some(s => s.includes(lowerFilterText))
  );
};

export const filterIcons = (icons: IconTabItem[], filterText: string) => {
  const filterIcons = (item: DistributeUnion<IconTabItem>) =>
    item.iconName?.includes(filterText) ||
    item.synonyms?.some(s => s.includes(filterText)) ||
    item.path?.toLowerCase().includes(filterText) ||
    iconDisplayName(item).toLowerCase().includes(filterText.toLowerCase());
  return icons.filter(filterIcons);
};

export const getIcon = (name: string) => {
  return ShapeIcon.iconDB[name];
};

export const iconDataForObject = async (
  object: fabric.Object
): Promise<IconTabItem | undefined> => {
  if (isShapeIcon(object)) {
    return object.shape.iconData();
  }

  if (isShapeSpriteIcon(object)) {
    return await SpriteSvg.getInstance().iconDataForObject(object);
  }
};

export const tranlsateIconData = (icon: IconTabItem) => {
  if (isSpriteIcon(icon)) {
    return icon.path;
  }
  return `${icon.prefix}/${icon.iconName}`;
};

export const iconSetForObject = async (object: fabric.Object) => {
  if (isShapeIcon(object)) {
    return iconList();
  }

  if (isShapeSpriteIcon(object)) {
    return await SpriteSvg.getInstance().iconSetForItem(object.iconName);
  }

  // Satisfy ts by avoiding undefined return
  return [];
};

export const IconSearchBox = ({
  onChange,
  value,
  style,
  placeholder,
  onClick,
  icons,
  currentIcon,
  columns = 8,
  columnWidth,
  controller,
}: {
  onChange: (value: string) => void;
  value: string;
  style: React.CSSProperties;
  placeholder: string;
  onClick: (icon: IconTabItem) => void;
  icons: IconTabItem[];
  currentIcon: IconTabItem | null | undefined;
  columns?: number;
  columnWidth?: number;
  controller: WhiteboardController;
}) => {
  let current = '';
  if (currentIcon) {
    current = isSpriteIcon(currentIcon)
      ? currentIcon.path
      : `${currentIcon.prefix}/${currentIcon.iconName}`;
  }

  return (
    <div>
      <SearchBox
        onChange={onChange}
        value={value}
        style={style}
        placeholder={placeholder}
      />
      <div
        className={classNames(styles.minimalScrollbar, styles.iconPickerPopup)}
      >
        <ToolbarMenu columns={columns} columnWidth={columnWidth}>
          {icons.map(icon => {
            const key = isFaIcon(icon)
              ? `${icon.prefix}/${icon.iconName}`
              : icon.path;

            const stylus = isSpriteIcon(icon)
              ? controller.stylusSpriteIcon
              : controller.stylusIcon;

            const className = stylus.toolButtonClass();

            const name = iconDisplayName(icon);

            return (
              <WithHint key={key} name={name}>
                <ToolButton
                  className={className}
                  active={current === key}
                  key={key}
                  onClick={() => onClick(icon)}
                  testId={testId(iconDisplayName(icon))}
                >
                  {iconComponent(icon)}
                </ToolButton>
              </WithHint>
            );
          })}
        </ToolbarMenu>
      </div>
    </div>
  );
};

export const normalizeIcon = (icon: IconWithOffset): NormalizedIcon => {
  const [reference, x = 0, y = 0] = Array.isArray(icon) ? icon : [icon];
  return { reference, offset: { x, y } };
};

export const getIconData = (iconName?: IconWithOffset) => {
  if (!iconName) {
    return;
  }

  const { reference = undefined, ...iconData } = normalizeIcon(iconName);
  if (!reference) {
    throw new Error('Invalid icon name');
  }

  if (typeof reference === 'string') {
    const data = ShapeIcon.iconDB[reference];
    return {
      ...iconData,
      type: 'path' as const,
      path: data.icon[4],
      width: data.icon[0],
      height: data.icon[1],
    };
  } else {
    const width = parseInt(reference.svg.match(/width="(\d+)"/)?.[1] ?? '50');
    const height = parseInt(reference.svg.match(/height="(\d+)"/)?.[1] ?? '50');

    return {
      ...iconData,
      type: 'svg' as const,
      svg: reference.svg,
      width,
      height,
    };
  }
};

const preparedImageCache = new Map<string, Promise<PreparedIcon>>();

/** Prepares an icon for rendering on canvas. If it's an SVG, creates an img element and decodes
 * it before returning. If it's a path, just returns the path data.
 */
export const getPreparedIcon = async (
  iconName: IconWithOffset,
  stroke: string
): Promise<PreparedIcon | undefined> => {
  const iconData = getIconData(iconName);
  if (!iconData) {
    return;
  }

  if (iconData.type === 'path') {
    return iconData;
  }

  const colored = iconData.svg.replace(/currentColor/g, stroke);
  const cacheKey = colored;
  const encoded = encodeURIComponent(colored);
  if (!preparedImageCache.get(cacheKey)) {
    preparedImageCache.set(
      cacheKey,
      new Promise((resolve, reject) => {
        const img = new Image();
        img.src = `data:image/svg+xml;charset=utf-8,${encoded}`;
        img
          .decode()
          .then(() => resolve({ ...iconData, img }))
          .catch(reject);
      })
    );
  }
  return await preparedImageCache.get(cacheKey);
};

/** Renders an icon from the iconDb on canvas, in the context of a fabric object. Assumes the canvas
 * context is already translated to the object's position.
 * @param ctx Canvas rendering context
 * @param iconData prepared icon data from either the iconDb or a svg string
 * @param iconWidth rendered icon width in pixels (height is derived from the aspect ratio)
 * @param align Position of the icon relative within the object
 * @param object the fabric object to render the icon on
 */
export const renderObjectIcon = (
  ctx: CanvasRenderingContext2D,
  iconData: PreparedIcon,
  iconWidth: number,
  align: 'center' | [number | 'center', number | 'center'],
  {
    width,
    height,
    borderOpacity,
    stroke,
    scaleX,
    scaleY,
  }: Pick<
    fabric.Object,
    'width' | 'height' | 'borderOpacity' | 'stroke' | 'scaleX' | 'scaleY'
  >
) => {
  const { x, y } = iconData.offset; // adjustments to the icon's position
  const iconHeight = (iconWidth * iconData.height) / iconData.width;
  const iconScale = iconWidth / iconData.width;
  const scaledWidth = width * scaleX;
  const scaledHeight = height * scaleY;

  ctx.save();

  const [alignX, alighY] = Array.isArray(align) ? align : [align, align];

  // icon adjustments relative to the size of the icon
  const adjustX = iconWidth * x;
  const adjustY = iconHeight * y;

  // position of the icon relative to the centre of the object
  const positionX =
    alignX === 'center' ? -iconWidth / 2 : (alignX ?? 0) - scaledWidth / 2;
  const positionY =
    alighY === 'center' ? -iconHeight / 2 : (alighY ?? 0) - scaledHeight / 2;

  ctx.translate((adjustX + positionX) / scaleX, (adjustY + positionY) / scaleY);
  ctx.scale(iconScale / scaleX, iconScale / scaleY);

  ctx.globalAlpha = borderOpacity ?? 1;

  if (iconData.type === 'svg') {
    ctx.drawImage(iconData.img, 0, 0, iconData.width, iconData.height);
  } else {
    const path = ShapeIcon.getPath(ctx, iconData.path);
    ctx.fillStyle = stroke ?? '';
    ctx.fill(path);
  }

  ctx.restore();
};

interface PathIconProps {
  className?: string;
  icon: IconWithOffset;
  fillContainer?: boolean;
}
export const PathIcon: React.FC<PathIconProps> = ({
  icon,
  className,
  fillContainer,
}) => {
  const iconData = getIconData(icon);
  if (!iconData) {
    return null;
  }

  const { x, y } = iconData.offset;

  if (iconData.type === 'svg') {
    const left = (x ?? 0) * 100;
    const top = (y ?? 0) * 100;
    const svgString = iconData.svg.replace(
      '<svg',
      `<svg style="position: relative;left: ${left}%;top: ${top}%"`
    );

    return svgToIcon(svgString, { className, fillContainer });
  }

  const d = iconData.path;

  return svgPathToIcon(
    {
      d: Array.isArray(d) ? d.join(' ') : d,
      width: iconData.width,
      height: iconData.height,
      fillRule: 'evenodd',
    },
    { className, x, y, fillContainer }
  );
};
